<template>
    <accordion
        class="drs-shop"
        header-class="drs-shop__header"
        :class="{ selected: location.selected }"
        :is-active="expandedShopId === shop.id && expandedTariffId === tariff.id"
    >
        <template #header>
            <ui-checkbox-input
                class="text-sm"
                :name="`shop-${shop.id}`"
                :label="`${shop.full_name}, ${shop.fullAddress}`"
                :value="location.selected"
                @change="toggleShop"
            />
        </template>
        <template #expand-button>
            <div class="text-md cursor-pointer nowrap" @click="toggleShopExpand">
                <a-icon class="pr-1" :icon="expandedShopId === shop.id ? 'ArrowUp' : 'ArrowDown'" />
                <strong v-text="'Konfiguruj'" />
            </div>
        </template>
        <template #body>
            <div class="drs-shop-table">
                <div class="drs-shop-table__row --header">
                    <div />
                    <div v-text="`Cena netto / ${$options.TIMELINES_NOUNS[tariff.timeline]}`" />
                </div>
                <div class="drs-shop-table__row --no-qty">
                    <div>
                        <h5 class="mb-0" v-text="tariff.name" />
                        <p class="text-primary mb-0" v-html="tariff.description_long" />
                    </div>
                    <div v-if="isCartLoading" class="drs-shop-table__price">
                        <button-spinner
                            status="loading"
                        />
                    </div>
                    <money-input v-else
                        class="mb-0"
                        disabled
                        :price="shopPrice"
                    />
                </div>
                <div class="drs-shop-table__row --no-qty">
                    <div class="d-flex gap-1">
                        <a-switch
                            v-tooltip="areAdditionalDevicesDisabled ? 'Opcja niedostępna' : ''"
                            class="mt-2"
                            :checked="location.additionalDevices"
                            :disabled="areAdditionalDevicesDisabled"
                            @change="updateAdditionalDevices"
                        />
                        <div>
                            <h5
                                class="mb-0 cursor-pointer"
                                v-text="'Opcje dodatkowe'"
                                @click="updateAdditionalDevices(!location.additionalDevices)"
                            />
                            <p
                                class="text-primary cursor-pointer"
                                v-text="`
                                    Stała kwota w wysokości
                                    ${$options.filters.currency(devicesPrice)}
                                    / ${$options.TIMELINES_NOUNS[$options.TIMELINES.MONTHLY]}
                                    za dowolną liczbę urządzeń w ramach limitu:
                                `"
                                @click="updateAdditionalDevices(!location.additionalDevices)"
                            />

                            <ul class="text-primary pl-3">
                                <li
                                    v-for="(optional, oIndex) in tariff.optionals.list"
                                    v-text="optional.name"
                                    :key="oIndex"
                                />
                            </ul>
                        </div>
                    </div>
                    <div />
                </div>
            </div>
        </template>
    </accordion>
</template>

<script>
import { mapGetters } from 'vuex'
import cloneDumb from '@/helpers/cloneDumb'
import { TIMELINES, TIMELINES_NOUNS } from '@/utils/constants/drs/tariffs'
import { CONFIG_MODES } from '@/store/modules/drs/tariffs.module'

export default {
    TIMELINES_NOUNS,

    TIMELINES,

    props: {
        shop: {
            type: Object,
            required: true
        },
        tariff: {
            type: Object,
            required: true
        },
        expandedTariffId: {
            type: [Number, null],
            required: true
        },
        expandedShopId: {
            type: [Number, null],
            required: false
        }
    },

    computed: {
        ...mapGetters('drsTariffs', ['selectedTariff', 'configMode', 'tariffsCopy', 'cart', 'isCartLoading']),
        ...mapGetters('drs', ['getSubscriptionByLocationId']),

        location() {
            return this.tariff.locations.find(loc => loc.id === this.shop.id)
        },

        shopCart() {
            return (this.cart?.locations || []).find(loc => loc.bo_location_id === this.shop.id) || {}
        },

        shopPrice() {
            if (!this.shopCart || !this.shopCart.payment_price_net) {
                return 0
            }

            return this.shopCart.payment_price_net / 100
        },

        isEditMode() {
            return this.configMode === CONFIG_MODES.EDIT
        },

        areChangesMade() {
            if (!this.tariffsCopy) {
                return false
            }

            const selectedLoc = this.selectedTariff?.locations.find(loc => loc.id === this.shop.id) || {}
            const copyLoc = this.tariffsCopy?.locations.find(loc => loc.id === this.shop.id) || {}
            return selectedLoc.additionalDevices !== copyLoc.additionalDevices
        },

        shopSubscription() {
            return this.$store.getters['drs/getSubscriptionByLocationId'](this.shop.id)
        },

        devicesPrice() {
            return this.tariff.devices_discounted_price || this.tariff.devices_price
        },

        areAdditionalDevicesDisabled() {
            if (this.configMode !== CONFIG_MODES.EDIT) {
                // If we're not in edit mode, additional devices are always enabled
                return false
            }

            const currentSub = this.$store.getters['drs/getSubscriptionByLocationId'](this.shop.id)
            if (!currentSub || Array.isArray(currentSub)) {
                // Only check if the user has a subscription for this location if we're in edit mode
                return false
            }

            // Search for the shop in the tariffsCopy
            const tariffShop = ((this.tariffsCopy || {}).locations || []).find(loc => loc.id === this.shop.id)
            if (!tariffShop) {
                return false
            }

            return this.tariff.timeline === TIMELINES.YEARLY &&
                this.tariff.id === currentSub.tariff_id &&
                tariffShop.additionalDevices
        }
    },

    methods: {
        toggleShop() {
            const tariff = cloneDumb(this.selectedTariff)
            const location = tariff.locations.find(el => el.id === this.shop.id)
            location.selected = !location.selected

            this.$store.commit('drsTariffs/UPDATE_TARIFFS', tariff)
        },

        toggleShopExpand() {
            this.$emit('expand-shop', this.shop.id)
        },

        updateAdditionalDevices(value) {
            if (this.areAdditionalDevicesDisabled) {
                return
            }

            const tariff = cloneDumb(this.selectedTariff)
            const location = tariff.locations.find(el => el.id === this.shop.id)

            const newValue = value === undefined ? !location.additionalDevices : value

            if (newValue === false) {
                // If user disables additional devices, reset device types
                this.$set(location, 'deviceTypes', [{
                    type: null,
                    qty: 0
                }])
            }
            this.$set(location, 'additionalDevices', newValue)

            this.$store.commit('drsTariffs/UPDATE_TARIFFS', tariff)
        },

        setInitialAdditionalDevices() {
            const location = this.tariff.locations.find(el => el.id === this.shop.id)
            if (!this.shopSubscription || location.additionalDevices !== null) {
                return
            }

            // Check if location has additional devices already set
            const devicesNumber = this.shopSubscription.device_type_subscriptions.reduce((acc, device) => {
                if (device.device_type === 'md') {
                    return acc
                }
                return acc + device.amount
            }, 0)

            // Set initial additional devices to true if there's more than 1
            this.updateAdditionalDevices(devicesNumber > 1)
        }
    },

    created() {
        this.setInitialAdditionalDevices()
    }
}
</script>

<style lang="scss">
@import '../../../styles/_custom.scss';
@import '~bootstrap/scss/mixins';

.drs-shop {
    --padding: 1.25rem;

    .drs-shop__header {
        padding: var(--padding);
        flex-wrap: wrap;
    }

    &.selected {
        background-color: var(--active-promo-bg);
        border: 2px solid var(--color-primary);

        &:hover {
            border-width: 2px;
        }

        .drs-shop__header {
            padding: calc(var(--padding) - 1px);
        }
    }
}

.drs-shop-table {
    border-collapse: collapse;
    width: 100%;

    &__price {
        display: flex;
        align-items: center;
        justify-content: center;
        height: 49px;
    }

    &__row {
        display: flex;
        gap: 0.5rem;

        + .drs-shop-table__row {
            margin-top: 0.75rem;
        }

        &.--header {
            text-transform: uppercase;
            letter-spacing: 0.08rem;
            font-size: 10px;
            margin-bottom: 1rem;
        }

        &.--optional {
            align-items: center;
        }

        > :nth-child(1) {
            width: 100%;
        }
        > :nth-child(2) {
            width: 6rem;
            min-width: 6rem;
        }
    }

    @include media-breakpoint-down(xs) {
        &__row {
            &.--header {
                display: none;
            }
            &.--no-qty {
                > :nth-child(2) {
                    display: none;
                }
            }
        }
    }
}
</style>